<template lang="pug">
AppScrollableTable(:has-fixed-column="false")
  AppTable(
    :table-data="clients"
    :selectable="canArchive"
    hoverable
    :loading="showTableLoading"
    @selected="selectedClients"
  )
    template(#empty)
      h4.clients-empty.text-muted.text-left No clients

    AppTableColumn(
      v-slot="{ row }"
      header="Name"
    )
      a.client-dialog-trigger.client-table-link.a-tooltip(
        data-testid="client-name-link"
        @click="openEditDialog(row.id, 'general')"
      )
        AppAvatarInitial(:text="row.name")
        | {{ row.name }}

    AppTableColumn(
      v-slot="{ row }"
      header="Budget"
    )
      AppHoverLink(
        :enabled="isActiveClient(row) && row.permissions.can_manage_client_budget"
        title="Edit this client's budget"
        @click="openEditDialog(row.id, 'budget')"
      )
        template(v-if="row.budget_data && row.budget_data.can_manage")
          template(v-if="hasBudget(row.budget_data) && budgetsVisible")
            .budget-info.line-height-x2.a-tooltip.inline-block(
              v-tooltip="tooltipOptions"
              :data-original-title="budgetProgressHint(row.budget_data)"
              data-tipclass="tooltip-max-w"
            )
              AppProgressBar(
                :tracked="row.budget_data.spent"
                :total="row.budget_data.amount"
              )
          template(v-else)
            small.text-lighter Budget:
            small.ml-5 none

    AppTableColumn(
      v-slot="{ row }"
      header="Auto-invoicing"
    )
      AppHoverLink(
        :enabled="isActiveClient(row) && invoicesVisible"
        title="Edit this client's auto invoicing settings"
        @click="openEditDialog(row.id, 'invoicing')"
      )
        template(v-if="autoInvoicingEnabled(row)")
          .auto-invoicing-settings.line-height-x2.a-tooltip.inline-block(
            v-tooltip="tooltipOptions"
            :data-original-title="autoInvoicingTooltip(row)"
          )
            i.hi.hi-16(:class="autoInvoicingIcon(row)")
        template(v-else)
          span Off

    AppTableColumn(
      v-slot="{ row }"
      header=""
    )
      ClientsTableActions(
        :client="row"
        @open-edit-dialog="openEditDialog"
      )
</template>

<script>
import { ACTIVE_CLIENT_STATUS, BUDGET_TYPE_COST } from '~/app_constants';
import { mapGetters, mapState, mapActions } from 'vuex';
import { isNil } from 'lodash-es';
import ClientsTableActions from './ClientsTableActions.vue';
import { formatDuration } from '~/utils/time_utils';

export default {
  name: 'ClientsTable',
  components: { ClientsTableActions },
  data: () => ({
    tooltipOptions: { placement: 'bottom', html: true, container: 'body' },
  }),
  computed: {
    ...mapState({
      canArchive: 'can_archive',
      organization: 'organization',
      clients: 'clients',
    }),
    ...mapGetters([
      'showTableLoading',
      'teamsVisible',
      'activeView',
      'budgetsVisible',
      'invoicesVisible',
      'orgAutoInvoicingEnabled',
    ]),
  },
  mounted() {
    const url = new URL(window.location.href);
    const searchParams = new URLSearchParams(url.search);
    const clientId = searchParams.get('client_id');

    if (clientId) {
      this.openEditDialog(clientId, 'general');
    }
  },
  methods: {
    ...mapActions(['setBatchActionIds', 'setFilterLoadTable']),
    openEditDialog(clientId, tab) {
      this.$emit('open-edit-dialog', clientId, tab);
    },
    budgetProgressHint(budget) {
      let formattedBudgetAmount;
      let formattedBudgetSpent;
      let formattedBudgetRemaining;
      const progress = budget.spent / Number(budget.amount);
      const budgetProgressPercent = parseInt(progress * 100);
      const budgetRemainingPercent = parseInt(100 - progress * 100);

      if (budget.type === BUDGET_TYPE_COST) {
        formattedBudgetAmount = budget.amount.formatMoney(this.organization.currency);
        formattedBudgetSpent = Number(budget.spent).formatMoney(this.organization.currency);
        formattedBudgetRemaining = Number(budget.remaining).formatMoney(this.organization.currency);
      } else {
        formattedBudgetAmount = formatDuration(budget.amount);
        formattedBudgetSpent = formatDuration(budget.spent);
        formattedBudgetRemaining = formatDuration(budget.remaining);
      }

      const spent = `<strong>Spent:</strong> ${formattedBudgetSpent}/${formattedBudgetAmount} <strong>(<em>${budgetProgressPercent}%</em>)</strong>`;
      const remaining = `<strong>Remaining:</strong> ${formattedBudgetRemaining} <strong>(<em>${budgetRemainingPercent}%</em>)</strong>`;

      return `<div class='text-left'>${spent}<br/>${remaining}</div>`;
    },
    hasBudget(budget) {
      return !isNil(budget.type) && !isNil(budget.amount);
    },
    selectedClients(selected) {
      const clientIds = selected.map(({ id }) => id);

      this.setBatchActionIds(clientIds);
    },
    isActiveClient(client) {
      return client.status === ACTIVE_CLIENT_STATUS;
    },
    autoInvoicingTooltip(client) {
      if (client.auto_invoicing_enabled) {
        return 'Enabled with custom settings';
      }
      if (this.orgAutoInvoicingEnabled) {
        return 'Enabled with organization settings';
      }

      return '';
    },
    autoInvoicingIcon(client) {
      if (client.auto_invoicing_enabled) {
        return 'hi-toggles';
      }
      if (this.orgAutoInvoicingEnabled && client.use_organization_auto_invoicing) {
        return 'hi-organization';
      }

      return '';
    },
    autoInvoicingEnabled(client) {
      return (
        this.isActiveClient(client) &&
        (client.auto_invoicing_enabled || (this.orgAutoInvoicingEnabled && client.use_organization_auto_invoicing))
      );
    },
  },
};
</script>
